import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "bb_cart_v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const add = useCallback((product) => {
        setItems((prev) => {
            const idx = prev.findIndex((p) => p.id === product.id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
                return next;
            }
            return [
                ...prev,
                {
                    id: product.id,
                    name: product.name_sq || product.name_en || product.name,
                    price_all: product.price_all,
                    image: product.image,
                    weight: product.weight,
                    weight_grams: product.weight_grams ?? null,
                    qty: 1,
                },
            ];
        });
    }, []);

    const updateQty = useCallback((id, qty) => {
        setItems((prev) =>
            prev
                .map((p) => (p.id === id ? { ...p, qty: Math.max(0, qty) } : p))
                .filter((p) => p.qty > 0),
        );
    }, []);

    const remove = useCallback((id) => {
        setItems((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    // Clear cart whenever the user logs out (event dispatched by AuthContext.logout)
    useEffect(() => {
        const handleLogout = () => setItems([]);
        window.addEventListener("bazaar:logout", handleLogout);
        return () => window.removeEventListener("bazaar:logout", handleLogout);
    }, []);

    const subtotal = useMemo(
        () => items.reduce((s, p) => s + p.price_all * p.qty, 0),
        [items],
    );
    const count = useMemo(
        () => items.reduce((s, p) => s + p.qty, 0),
        [items],
    );

    const value = useMemo(
        () => ({ items, add, updateQty, remove, clear, subtotal, count, open, setOpen }),
        [items, add, updateQty, remove, clear, subtotal, count, open],
    );

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
